import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';

/**
 * The AI trend analysis card's data, shared by today's card and the
 * redesigned one: fetched on mount; Refresh forces a regeneration; after a
 * successful load the card opens expanded; on an error or an empty answer
 * there is nothing to show (the card hides). A second Refresh while one is
 * running does nothing.
 */
export default function useTrendAnalysis() {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);

    const fetchAnalysis = useCallback(async (forceRefresh = false) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = forceRefresh
                ? await apiService.refreshTrendAnalysis()
                : await apiService.getTrendAnalysis();
            if (data) {
                setAnalysis(data);
                setCollapsed(false);
            } else {
                setError('Could not generate analysis');
            }
        } catch {
            setError('Failed to load analysis');
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

    const lines = (analysis?.analysis || '').split('\n').filter((l) => l.trim());

    return {
        analysis, loading, error, collapsed, setCollapsed, lines,
        refresh: () => fetchAnalysis(true),
    };
}
